import React, { useState } from "react";
import { Link } from "react-router-dom";
import RegisterStudentForm from "./RegisterStudentForm";
import RegisterStaffForm from "./RegisterStaffForm";
import { GraduationCap, Briefcase, ArrowRight, Home } from "lucide-react";
import { motion } from "framer-motion";

const Register = () => {
  const [selectedRole, setSelectedRole] = useState("");

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  if (selectedRole) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="container mx-auto px-4 py-8">
          {selectedRole === "student" && <RegisterStudentForm />}
          {selectedRole === "staff" && <RegisterStaffForm />}

          <button
            className="mt-6 mx-auto block bg-white text-gray-700 font-semibold px-8 py-3 rounded-xl 
  border-2 border-gray-200 hover:border-uzima-maroon-500 hover:shadow-lg transition-all"
            onClick={() => setSelectedRole("")}
          >
            ← Back to Role Selection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex relative">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/uzimabanner.jpg"
          alt="Mount Kenya University"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = "/images/counselling illustration.webp";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-uzima-maroon-50/95"></div>
      </div>

      {/* Role Selection Container */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <img
                src="/uzimalogo.jpg"
                alt="Uzima Logo"
                className="h-12 w-12 object-contain"
                onError={(e) => {
                  e.target.src = "/uzimalogo.jpg";
                }}
              />
              <div>
                <h1
                  className="text-2xl font-bold bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 
  bg-clip-text text-transparent"
                >
                  Uzima
                </h1>
                <p className="text-xs text-gray-600">Counseling Center</p>
              </div>
            </Link>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Create Your Account
            </h2>
            <p className="text-gray-600">
              Select your role to get started with professional counseling
              support
            </p>
          </div>

          {/* Role Selection Cards */}
          <div className="space-y-4">
            {/* Student Card */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleRoleSelect("student")}
              className="w-full flex items-center justify-between p-6 bg-gradient-to-br from-blue-50
  to-indigo-50 border-2 border-uzima-maroon-200 rounded-2xl hover:border-blue-400 hover:shadow-lg transition-all        
  group"
            >
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-br from-uzima-maroon-600 to-uzima-maroon-700 p-3 rounded-xl">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-bold text-gray-900">
                    Student / Alumni
                  </h3>
                  <p className="text-sm text-gray-600">
                    Current students and alumni
                  </p>
                </div>
              </div>
              <ArrowRight
                className="w-5 h-5 text-gray-400 group-hover:text-uzima-maroon-500 
  group-hover:translate-x-1 transition-all"
              />
            </motion.button>

            {/* Staff Card */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleRoleSelect("staff")}
              className="w-full flex items-center justify-between p-6 bg-gradient-to-br from-purple-50        
  to-pink-50 border-2 border-purple-200 rounded-2xl hover:border-purple-400 hover:shadow-lg transition-all      
  group"
            >
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-br from-purple-600 to-pink-600 p-3 rounded-xl">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-bold text-gray-900">
                    Staff / Faculty
                  </h3>
                  <p className="text-sm text-gray-600">
                    Teaching and non-teaching staff
                  </p>
                </div>
              </div>
              <ArrowRight
                className="w-5 h-5 text-gray-400 group-hover:text-purple-600 
  group-hover:translate-x-1 transition-all"
              />
            </motion.button>
          </div>

          {/* Sign In Link */}
          <p className="mt-8 text-center text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-semibold text-uzima-maroon-500 hover:text-uzima-maroon-600 transition"
            >
              Sign in here
            </Link>
          </p>

          {/* Back to Home Button */}
          <div className="mt-4 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-uzima-maroon-600 transition"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
